import { api } from '@/utils/api';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSearchbar,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar
} from '@ionic/react';
import { t } from 'i18next';
import { checkmark, chevronForward } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import GolfIcon from './GolfIcon';

interface GolfBrand {
  id: number;
  name: string;
  iconUrl?: string;
}

interface GolfModel {
  id: number;
  brandId: number;
  name: string;
  type: string;
  loft?: number;
}

interface ClubDetails {
  id: number;
  brandId: number;
  brandName?: string;
  modelId: number;
  modelName?: string;
  customModelName?: string;
  type: string;
  clubNumber?: number;
  loft?: number;
  notes?: string;
  inBag: boolean;
  performance?: {
    averageDistance?: number;
  };
}

interface ClubDetailsModalProps {
  clubId?: number;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

const ClubDetailsModal: React.FC<ClubDetailsModalProps> = ({
  clubId,
  isOpen,
  onClose,
  onUpdate,
  triggerRef,
}) => {
  const [club, setClub] = useState<ClubDetails | null>(null);
  const [brands, setBrands] = useState<GolfBrand[]>([]);
  const [models, setModels] = useState<GolfModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const modalRef = useRef<HTMLIonModalElement>(null);

  const [activePage, setActivePage] = useState<'details' | 'brands'>('details');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredBrands, setFilteredBrands] = useState<GolfBrand[]>([]);

  const [formData, setFormData] = useState<Partial<ClubDetails>>({
    brandId: 0,
    modelId: 0,
    customModelName: '',
    notes: '',
    inBag: true,
    performance: {
      averageDistance: 0
    }
  });

  useEffect(() => {
    const checkOwnership = async () => {
      if (!clubId) return;
      
      try {
        const user = await getCurrentUser();
        const club = await api.get(`/golf-gear/${clubId}`);
        setCanEdit(user?.uid === club.data.userId);
      } catch (error) {
        console.error('Error checking club ownership:', error);
        setCanEdit(false);
      }
    };
    
    checkOwnership();
  }, [clubId]);

  useEffect(() => {
    if (isOpen && clubId) {
      fetchClubDetails();
    }
  }, [isOpen, clubId]);

  useEffect(() => {
    if (brands.length > 0) {
      if (searchQuery.trim() === '') {
        setFilteredBrands(brands);
      } else {
        const query = searchQuery.toLowerCase();
        setFilteredBrands(brands.filter(brand =>
          brand.name.toLowerCase().includes(query)
        ));
      }
    }
  }, [brands, searchQuery]);

  const fetchClubDetails = async () => {
    if (!clubId) return;

    try {
      setLoading(true);

      // Fetch club details
      const clubResponse = await api.get(`/golf-gear/${clubId}`);

      if (!clubResponse.data) {
        throw new Error('Failed to fetch club details');
      }

      const clubData = clubResponse.data;
      setClub(clubData);
      setFormData(clubData);

      // Fetch brands
      const brandsResponse = await api.get('/golf-gear/brands');

      if (!brandsResponse.data) {
        throw new Error('Failed to fetch brands');
      }
      setBrands(brandsResponse.data);

      // Fetch models
      const modelsResponse = await api.get('/golf-gear/models');

      if (!modelsResponse.data) {
        throw new Error('Failed to fetch models');
      }
      setModels(modelsResponse.data);
    } catch (err) {
      console.error('Error fetching club details:', err);
      setError('Failed to load club details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveClub = async () => {
    if (!clubId || !canEdit) return;

    try {
      setSaving(true);

      // Make a deep copy to avoid reference issues
      const dataToSave = JSON.parse(JSON.stringify(formData));

      // Ensure performance data is properly formatted
      if (dataToSave.performance && typeof dataToSave.performance.averageDistance === 'string') {
        dataToSave.performance.averageDistance = parseInt(dataToSave.performance.averageDistance) || 0;
      }

      const response = await api.put(`/golf-gear/${clubId}`, dataToSave);

      if (!response.data) {
        throw new Error('Failed to update club');
      }

      setSuccessMessage('Club mis à jour avec succès!');
      setClub({ ...club, ...dataToSave } as ClubDetails);

      if (onUpdate) {
        onUpdate();
        onClose();
      }

    } catch (err) {
      console.error('Error updating club:', err);
      setError('Failed to update club. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteClub = async () => {
    if (!clubId || !canEdit) return;

    try {
      setSaving(true);

      const response = await api.delete(`/golf-gear/${clubId}`);

      if (!response.data) {
        throw new Error('Failed to delete club');
      }

      setSuccessMessage('Club supprimé avec succès!');

      if (onUpdate) {
        onUpdate();
      }

      // Close modal after successful deletion
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (err) {
      console.error('Error deleting club:', err);
      setError('Failed to delete club. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getClubName = () => {
    if (!club) return '';

    if (club.type === 'driver') {
      return 'Driver';
    } else if (club.type === 'putter') {
      return 'Putter';
    } else if (club.type === 'wedge' && club.loft) {
      return `${club.loft}° Wedge`;
    } else if (club.type === 'wedge') {
      const wedgeTypes: Record<number, string> = {
        0: 'Pitching Wedge',
        1: 'Gap Wedge',
        2: 'Sand Wedge',
        3: 'Lob Wedge',
      };
      return wedgeTypes[club.clubNumber || 0] || 'Wedge';
    } else {
      return `${club.clubNumber} ${club.type.charAt(0).toUpperCase() + club.type.slice(1)}`;
    }
  };

  const getBrandModel = () => {
    if (!club) return '';

    const brand = club.brandName || '';
    const model = club.customModelName || club.modelName || '';

    if (brand && model) {
      return `${brand} ${model}`;
    } else if (brand) {
      return brand;
    } else if (model) {
      return model;
    }

    return '';
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="club-details-modal"
    >
      <IonHeader>
        {activePage === 'details' && (
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onClose}>
                <GolfIcon name="close" />
              </IonButton>
            </IonButtons>
            <IonTitle>{getClubName()}</IonTitle>
            <IonButtons slot="end">
              {canEdit && (
                <IonButton onClick={() => saveClub()}>
                  {t('save')}
                </IonButton>
              )}
            </IonButtons>
          </IonToolbar>
        )}
        {activePage === 'brands' && (
          <IonToolbar className="mt-2">
            <IonSearchbar
              placeholder={t('search_brands')}
              cancelButtonText={t('cancel')}
              onIonCancel={() => setActivePage('details')}
              showCancelButton="always"
              className="rounded-lg"
              mode="ios"
              value={searchQuery}
              onIonInput={(e) => setSearchQuery(e.detail.value || '')}
              debounce={300}
            />
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <IonSpinner name="circular" />
            <p className="text-center text-gray-500 mt-4">Chargement des détails...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-center text-gray-500 mb-4">{error} id:{clubId}</p>
            <IonButton onClick={() => window.location.reload()}>Réessayer</IonButton>
          </div>
        ) : activePage === 'brands' ? (
          <div className="space-y-4">
            <IonList className="rounded-lg overflow-hidden">
              {filteredBrands.map(brand => (
                <IonItem
                  key={brand.id}
                  button
                  detail={false}
                  onClick={() => {
                    handleInputChange('brandId', brand.id);
                    setSearchQuery("");
                    setActivePage('details');
                  }}
                  lines="full"
                >
                  <IonLabel>{brand.name}</IonLabel>
                  {formData.brandId === brand.id && (
                    <IonIcon icon={checkmark} slot="end" color="primary" />
                  )}
                </IonItem>
              ))}
            </IonList>
          </div>
        ) : club ? (
          <div className="space-y-4 ion-padding">
            <IonList lines="none" className="space-y-4">
              {canEdit && (
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <IonLabel className="block">Dans le sac</IonLabel>
                    <IonToggle
                      checked={formData.inBag}
                      onIonChange={e => handleInputChange('inBag', e.detail.checked)}
                      mode="ios"
                    />
                  </div>
                </div>
              )}
              {canEdit && (
                <div className="mb-4 space-y-2">
                  <IonLabel className="block">{t('brand')}</IonLabel>
                  <IonButton
                    fill="clear"
                    color="transparent"
                    className="w-full h-12 max-w-xs text-foreground border-input border rounded-md"
                    onClick={() => setActivePage('brands')}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-4">
                        {formData.brandId && brands.find(b => b.id === formData.brandId) ? (
                          <span>{brands.find(b => b.id === formData.brandId)?.name}</span>
                        ) : (
                          <span className="text-muted-foreground">{t('select_brand')}</span>
                        )}
                      </div>
                      <IonIcon icon={chevronForward} className="size-5" />
                    </div>
                  </IonButton>
                </div>
              )}
              {/* <div className="mb-4 space-y-2">
                <IonLabel className="block">{t('model')}</IonLabel>
                <IonItem className="custom-item" no-lines>
                  <IonSelect
                    value={formData.modelId}
                    onIonChange={e => handleInputChange('modelId', e.detail.value)}
                    interface="action-sheet"
                    placeholder="Sélectionner un modèle"
                    className="w-full"
                  >
                    {models
                      .filter(model => model.type === club.type)
                      .map(model => (
                        <IonSelectOption key={model.id} value={model.id}>
                          {model.name}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </IonItem>
              </div> */}

              {/* <div className="mb-4 space-y-2">
                <IonLabel className="block">Nom personnalisé</IonLabel>
                <IonItem className="custom-item" no-lines>
                  <IonInput
                    value={formData.customModelName}
                    onIonInput={e => handleInputChange('customModelName', e.target.value)}
                    placeholder="Optionnel"
                  />
                </IonItem>
              </div> */}

              {(club.type === 'wedge' || club.type === 'iron') && canEdit && (
                <div className="mb-4 space-y-2">
                  <IonLabel className="block">{t('loft')}</IonLabel>
                  <IonItem className="custom-item" no-lines>
                    <IonInput
                      type="number"
                      value={formData.loft}
                      onIonInput={e => handleInputChange('loft', e.target.value)}
                    />
                  </IonItem>
                </div>
              )}

              {canEdit && (
                <div className="mb-4 space-y-2">
                  <IonLabel className="block">Distance moyenne (m)</IonLabel>
                  <IonItem className="custom-item" no-lines>
                    <IonInput
                      type="number"
                      value={formData.performance?.averageDistance}
                      onIonInput={e => handleInputChange('performance', {
                        ...formData.performance,
                        averageDistance: parseInt(e.target.value || '0')
                      })}
                    />
                  </IonItem>
                </div>
              )}

              {canEdit && (
                <div className="mb-4 space-y-2">
                  <IonLabel className="block">{t('notes')}</IonLabel>
                  <IonItem className="custom-item" no-lines>
                    <IonTextarea
                      value={formData.notes}
                      onIonInput={e => handleInputChange('notes', e.target.value)}
                      rows={3}
                      placeholder="Notes personnelles sur ce club"
                    />
                  </IonItem>
                </div>
              )}
            </IonList>

            {canEdit && (
              <div className="flex flex-col space-y-4 mt-8">
                <IonButton
                  color="danger"
                  fill="outline"
                  onClick={deleteClub}
                  disabled={saving}
                  className="w-full mt-4"
                  expand="block"
                >
                  Supprimer ce club
                </IonButton>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-center text-gray-500">Aucun club sélectionné</p>
          </div>
        )}

        <IonToast
          isOpen={!!error}
          message={error || ''}
          duration={3000}
          onDidDismiss={() => setError(null)}
          position="bottom"
          color="danger"
        />

        <IonToast
          isOpen={!!successMessage}
          message={successMessage || ''}
          duration={2000}
          onDidDismiss={() => setSuccessMessage(null)}
          position="bottom"
          color="success"
        />
      </IonContent>
    </IonModal>
  );
};

export default ClubDetailsModal;
